import { api } from "./api";
import { pick } from "./utils.api";

// ─── Types ───────────────────────────────────────────────────────────────────

export type CreditType = "token" | "count" | "seat";

// One selectable feature for the package create/edit form. Served by
// GET FeatureCreditPackages/feature-options — a curated subset of FeatureKeys.All,
// not the full key list. creditType drives the display label.
export interface FeatureOption {
  featureKey: string;
  creditType: CreditType;
}

export interface FeatureCreditPackagePrice {
  currencyId: number;
  priceBeforeTax: number;
  vat: number;
  priceAfterTax: number;
}

export interface FeatureCreditPackageDto {
  id: number;
  name: string; // Hebrew display name
  nameEn: string; // English display name
  description: string | null;
  featureKey: string;
  creditType: CreditType; // derived server-side from featureKey (read-only)
  // Size of the pack, split by creditType: count/seat packs carry it in `amount`, token packs are
  // credit-denominated and carry it in `creditsGranted` (the other field is null). See
  // packageSizeLabel(), which is the one place this split is turned into copy.
  amount: number | null;
  creditsGranted: number | null;
  companyId: number | null; // null = global pack; number = company-specific
  isActive: boolean;
  sortOrder: number;
  prices: FeatureCreditPackagePrice[];
}

// Exactly one of amount / creditsGranted applies, decided by the featureKey's creditType: a token
// pack requires creditsGranted (> 0) and ignores amount; a count/seat pack requires amount (> 0) and
// must not carry creditsGranted. Both rules are enforced on POST *and* PUT, so an edit that switches
// featureKey across the token boundary has to switch which field it sends.
export interface FeatureCreditPackageCreateDto {
  name: string;
  nameEn: string;
  description?: string | null;
  featureKey: string;
  amount?: number | null;
  creditsGranted?: number | null;
  companyId?: number | null; // null = global
  isActive?: boolean; // default true
  sortOrder?: number;
  prices: FeatureCreditPackagePrice[]; // at most one per currencyId
}

// All fields optional; null/omitted = unchanged.
export type FeatureCreditPackageUpdateDto = Partial<FeatureCreditPackageCreateDto> & {
  isActive?: boolean;
};

export interface FeatureCreditBuyRequest {
  packageId: number;
  currencyId: number;
  // The only accepted value — there is no "self" any more. An admin buying credits for their own
  // wallet passes "company" alongside their own targetUserId.
  scope: "company";
  targetUserId?: number;
  // Double-submit guard. Generate once per checkout attempt, reuse verbatim on a retry of that same
  // attempt (network error / 409 purchase_in_progress / 429), regenerate on a definitive decline or
  // a deliberate new attempt. See useFeatureCreditPurchase.
  idempotencyKey?: string;
}

// The charge is synchronous, so this is the final outcome — no redirect, no webhook to wait for.
// Named DTO on a backend with no camelCase contract resolver configured, so the wire format is
// PascalCase; normalized defensively via pick() the same way the rest of this codebase already
// handles this backend's inconsistency (see utils.api.ts's pick()).
//
// Same amountGranted/creditsGranted split as FeatureCreditPurchaseDto — a token grant reports
// creditsGranted with amountGranted null, a count/seat grant the reverse. Never read one alone.
export interface FeatureCreditBuyResponse {
  purchaseId: number;
  paymentId: number | null;
  transactionUid: string | null;
  status: string;
  featureKey: string;
  creditType: CreditType;
  amountGranted: number | null;
  creditsGranted: number | null;
  // Set only for a seat-pack purchase (the recurring subscription it just created); null for every
  // other credit type. Lets the success banner offer a "Go to Seat Packs" next step.
  subscriptionId: number | null;
}

const normalizeBuyResponse = (raw: any): FeatureCreditBuyResponse => ({
  purchaseId: pick<number>(raw, "PurchaseId", "purchaseId", 0),
  paymentId: pick<number | null>(raw, "PaymentId", "paymentId", null),
  transactionUid: pick<string | null>(raw, "TransactionUid", "transactionUid", null),
  status: pick<string>(raw, "Status", "status", ""),
  featureKey: pick<string>(raw, "FeatureKey", "featureKey", ""),
  creditType: pick<CreditType>(raw, "CreditType", "creditType", "token"),
  amountGranted: pick<number | null>(raw, "AmountGranted", "amountGranted", null),
  creditsGranted: pick<number | null>(raw, "CreditsGranted", "creditsGranted", null),
  subscriptionId: pick<number | null>(raw, "SubscriptionId", "subscriptionId", null),
});

// GET /FeatureCredits/PaymentMethod — eligibility for the pack screen, and which card a purchase
// would be charged on. Same PascalCase-on-the-wire caveat as FeatureCreditBuyResponse above.
export interface FeatureCreditPaymentMethodResponse {
  canBuy: boolean;
  // Null when canBuy; otherwise "no_active_subscription" or "company_missing_billing_email".
  reason: string | null;
  // Nullable even when canBuy is true (an older subscription row may not carry it) — display detail
  // only, never a purchase blocker.
  cardLast4: string | null;
}

const normalizePaymentMethodResponse = (raw: any): FeatureCreditPaymentMethodResponse => ({
  canBuy: pick<boolean>(raw, "CanBuy", "canBuy", false),
  reason: pick<string | null>(raw, "Reason", "reason", null),
  cardLast4: pick<string | null>(raw, "CardLast4", "cardLast4", null),
});

export type FeatureCreditPurchaseStatus = "Pending" | "Completed" | "Failed" | "Refunded";

// A row in the feature-credit purchase history — GET /FeatureCredits/Purchases.
// Same amount/creditsGranted split as FeatureCreditPackageDto: a token purchase carries its size in
// creditsGranted (amountGranted null); a count/seat purchase carries it in amountGranted
// (creditsGranted null). pricePaid is null on legacy rows created before that column existed.
export interface FeatureCreditPurchaseDto {
  id: number;
  packageId: number;
  packageName: string;
  featureKey: string;
  creditType: CreditType;
  amountGranted: number | null;
  creditsGranted: number | null;
  pricePaid: number | null;
  companyId: number;
  targetUserId: number | null;
  targetUserName: string | null;
  currencyId: number;
  status: FeatureCreditPurchaseStatus;
  paymentId: number | null;
  invoiceNumber: string | null;
  invoiceUrl: string | null;
  createdByUserId: number;
  createdByName: string;
  createdAt: string;
  completedAt: string | null;
  refundedAt: string | null;
}

// ─── Seat subscriptions (recurring seat packs) ───────────────────────────────

// Only ever these two: an "Active" pack renews, a "PastDue" one failed its charge and is inside its
// grace window. Ended packs are not served here at all — they live in GET FeatureCredits/Purchases.
export type SeatSubscriptionStatus = "Active" | "PastDue";

export interface SeatSubscriptionDto {
  id: number;
  packageId: number;
  name: string;
  featureKey: string;
  seats: number;
  pricePerCycle: number;
  // A code/name string here, NOT the numeric currencyId used elsewhere — and nullable: the backend
  // resolves an orphaned CurrencyId to null and serves the pack anyway. Treating it as a plain
  // string is what renders "49 null" in the Price column; priceLabel() in utils/seatSubscriptions
  // owns the split.
  currency: string | null;
  status: SeatSubscriptionStatus;
  currentPeriodEnd: string; // Israel midnight expressed in UTC — render via formatDateInIsrael
  graceEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface CancelSeatSubscriptionResponse {
  // null = no paid time left; the pack ends at the next daily run rather than on a nameable day.
  effectiveAt: string | null;
}

const normalizeSeatSubscription = (raw: any): SeatSubscriptionDto => ({
  id: pick<number>(raw, "Id", "id", 0),
  packageId: pick<number>(raw, "PackageId", "packageId", 0),
  name: pick<string>(raw, "Name", "name", ""),
  featureKey: pick<string>(raw, "FeatureKey", "featureKey", ""),
  seats: pick<number>(raw, "Seats", "seats", 0),
  pricePerCycle: pick<number>(raw, "PricePerCycle", "pricePerCycle", 0),
  // Fallback null rather than "": pick coalesces with ??, so an empty string would masquerade as a
  // real currency and render as a trailing space instead of hitting priceLabel's guard.
  currency: pick<string | null>(raw, "Currency", "currency", null),
  // "Active" is the calm default on purpose — a missing status must not raise the past-due alarm.
  status: pick<SeatSubscriptionStatus>(raw, "Status", "status", "Active"),
  currentPeriodEnd: pick<string>(raw, "CurrentPeriodEnd", "currentPeriodEnd", ""),
  graceEndsAt: pick<string | null>(raw, "GraceEndsAt", "graceEndsAt", null),
  cancelAtPeriodEnd: pick<boolean>(raw, "CancelAtPeriodEnd", "cancelAtPeriodEnd", false),
});

const normalizeCancelResponse = (raw: any): CancelSeatSubscriptionResponse => ({
  effectiveAt: pick<string | null>(raw, "EffectiveAt", "effectiveAt", null),
});

// Manual-QA fixture for listSeatSubscriptions — see the commented-out queryFn on that endpoint
// below. Only read once that queryFn is uncommented; edit freely otherwise. Swap the array for any
// scenario's JSON straight out of "Manual Test Plan — Seat packs tab" in
// MANUAL_TEST_PLAN_PLAN_CHANGE_IMPACT_DIALOG.md — most of those states (PastDue, GraceEndsAt,
// CancelAtPeriodEnd) can't be produced from a real backend on demand.
const MOCK_SEAT_SUBSCRIPTIONS: any[] = [
  {
    Id: 1,
    PackageId: 10,
    Name: "Therapist seats",
    FeatureKey: "MAX_THERAPISTS",
    Seats: 5,
    PricePerCycle: 49,
    Currency: null,
    Status: "PastDue",
    CurrentPeriodEnd: "2026-10-11T21:00:00Z",
    GraceEndsAt: "2026-10-12T21:00:00Z",
    CancelAtPeriodEnd: true,
  },
    {
    Id: 1,
    PackageId: 10,
    Name: "Patients seats",
    FeatureKey: "MAX_THERAPISTS",
    Seats: 5,
    PricePerCycle: 49,
    Currency: "ILS",
    Status: "Active",
    CurrentPeriodEnd: "2026-10-11T21:00:00Z",
    GraceEndsAt: "2026-10-12T21:00:00Z",
    CancelAtPeriodEnd: false,
  },

];

export interface ListFeatureCreditPurchasesParams {
  companyId?: number; // SuperAdmin only — an admin's own companyId is enforced server-side otherwise.
  status?: FeatureCreditPurchaseStatus;
  targetUserId?: number;
  from?: Date;
  to?: Date;
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const featureCreditPackagesApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Arg is either the legacy bare `includeInactive` boolean (the catalog screen keeps passing that
    // shape) or an options object — `billingCurrencyOnly: true` narrows every pack's `prices` down to
    // the one row (if any) matching the caller's company's billing currency of record, the same
    // currency Buy enforces via its currency_mismatch guard. Only the buy screen may pass it: the
    // catalog/edit screen needs every currency a pack is priced in to populate its edit form.
    listFeatureCreditPackages: build.query<
      FeatureCreditPackageDto[],
      boolean | { includeInactive?: boolean; billingCurrencyOnly?: boolean } | void
    >({
      query: (arg) => {
        const opts = typeof arg === "object" && arg !== null ? arg : { includeInactive: !!arg };
        return {
          url: "FeatureCreditPackages",
          params: {
            includeInactive: !!opts.includeInactive,
            billingCurrencyOnly: !!opts.billingCurrencyOnly,
          },
        };
      },
      providesTags: ["FeatureCreditPackages"],
    }),

    getFeatureOptions: build.query<FeatureOption[], void>({
      query: () => ({ url: "FeatureCreditPackages/feature-options" }),
    }),

    getFeatureCreditPackage: build.query<FeatureCreditPackageDto, number>({
      query: (id) => ({ url: `FeatureCreditPackages/${id}` }),
      providesTags: (_result, _error, id) => [{ type: "FeatureCreditPackages", id }],
    }),

    createFeatureCreditPackage: build.mutation<
      FeatureCreditPackageDto,
      FeatureCreditPackageCreateDto
    >({
      query: (body) => ({ url: "FeatureCreditPackages", method: "POST", body }),
      invalidatesTags: ["FeatureCreditPackages"],
    }),

    updateFeatureCreditPackage: build.mutation<
      FeatureCreditPackageDto,
      { id: number; body: FeatureCreditPackageUpdateDto }
    >({
      query: ({ id, body }) => ({ url: `FeatureCreditPackages/${id}`, method: "PUT", body }),
      invalidatesTags: ["FeatureCreditPackages"],
    }),

    deleteFeatureCreditPackage: build.mutation<void, number>({
      query: (id) => ({ url: `FeatureCreditPackages/${id}`, method: "DELETE" }),
      invalidatesTags: ["FeatureCreditPackages"],
    }),

    buyFeatureCredits: build.mutation<FeatureCreditBuyResponse, FeatureCreditBuyRequest>({
      query: (body) => ({ url: "FeatureCredits/Buy", method: "POST", body }),
      transformResponse: normalizeBuyResponse,
      // A seat-type pack purchase creates a row this same GET serves, so its cache must be
      // invalidated too — otherwise a Seat Packs tab visited before the purchase keeps showing its
      // stale (pre-purchase) list until something else happens to refetch it.
      invalidatesTags: ["FeatureCreditPurchases", "SeatSubscriptions"],
    }),

    getPaymentMethod: build.query<FeatureCreditPaymentMethodResponse, void>({
      query: () => ({ url: "FeatureCredits/PaymentMethod" }),
      transformResponse: normalizePaymentMethodResponse,
    }),

    listFeatureCreditPurchases: build.query<
      FeatureCreditPurchaseDto[],
      ListFeatureCreditPurchasesParams | void
    >({
      query: (params) => ({
        url: "FeatureCredits/Purchases",
        params: {
          status: params?.status,
          targetUserId: params?.targetUserId,
          from: params?.from?.toISOString(),
          to: params?.to?.toISOString(),
        },
      }),
      providesTags: ["FeatureCreditPurchases"],
    }),

    // Served in CurrentPeriodEnd-ascending order, which is what the downgrade dialog needs and the
    // wrong order for the seat-packs screen — see sortSeatPacks in utils/seatSubscriptions, which
    // the tab applies itself rather than this transformResponse doing it for every consumer.
    listSeatSubscriptions: build.query<SeatSubscriptionDto[], void>({
      query: () => ({ url: "FeatureCredits/SeatSubscriptions" }),
      // A 204/empty body would otherwise make .map throw and turn an empty account into a crashed
      // tab — the one failure mode that looks identical to "you have no packs".
      transformResponse: (raw: any) =>
        (Array.isArray(raw) ? raw : []).map(normalizeSeatSubscription),
      providesTags: ["SeatSubscriptions"],

      // For manual QA — comment out `query`/`transformResponse` above and uncomment below to serve
      // MOCK_SEAT_SUBSCRIPTIONS instead of hitting the network.
      /*
      queryFn: () => ({ data: MOCK_SEAT_SUBSCRIPTIONS.map(normalizeSeatSubscription) }),
      */
    }),

    // Cancel-at-period-end: one local write, no provider call, idempotent. The invalidation is what
    // flips the row to "ends on <date>", so there is no optimistic local state to keep in sync.
    cancelSeatSubscription: build.mutation<CancelSeatSubscriptionResponse, number>({
      query: (id) => ({ url: `FeatureCredits/SeatSubscriptions/${id}/Cancel`, method: "POST" }),
      transformResponse: normalizeCancelResponse,
      invalidatesTags: ["SeatSubscriptions"],

      // For manual QA — pairs with the mock above so the cancel dialog and post-cancel banner
      // (test-plan §9-12) work with no real backend call. Comment out `query`/`transformResponse`
      // above and uncomment ONE line below.
      /*
      queryFn: () => ({ data: { effectiveAt: "2026-10-11T21:00:00Z" } }),                     // success, dated (§10)
      // queryFn: () => ({ data: { effectiveAt: null } }),                                    // success, imminent (§10.5)
      // queryFn: () => ({ error: { status: 404, data: { error: "unknown_subscription" } } }), // §12.1
      // queryFn: () => ({ error: { status: 429, data: {} } }),                                // §12.2
      // queryFn: () => ({ error: { status: 403, data: {} } }),                                // §12.3
      // queryFn: () => ({ error: { status: 500, data: {} } }),                                // §12.4
      */
    }),
  }),
});

export const {
  useGetFeatureOptionsQuery,
  useListFeatureCreditPackagesQuery,
  useGetFeatureCreditPackageQuery,
  useCreateFeatureCreditPackageMutation,
  useUpdateFeatureCreditPackageMutation,
  useDeleteFeatureCreditPackageMutation,
  useBuyFeatureCreditsMutation,
  useGetPaymentMethodQuery,
  useListFeatureCreditPurchasesQuery,
  useListSeatSubscriptionsQuery,
  useCancelSeatSubscriptionMutation,
} = featureCreditPackagesApi;
